import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { friendlyErrorMessage } from "../api/errors";
import { TextField } from "../components/TextField";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

function UserIcon() {
  return <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7"/><path d="M5.5 19c.8-3.1 3-4.7 6.5-4.7s5.7 1.6 6.5 4.7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" fill="none"><rect x="5.5" y="10" width="13" height="10" rx="2" stroke="currentColor" strokeWidth="1.7"/><path d="M8.5 10V7.8a3.5 3.5 0 117 0V10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>;
}

function EyeIcon({ crossed = false }: { crossed?: boolean }) {
  return <svg viewBox="0 0 24 24" fill="none"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/><circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.7"/>{crossed && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>}</svg>;
}

function ArrowRightIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

export function LoginPage() {
  const { login, sessionExpiredNotice } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { requiresPasswordChange } = await login(username, password);
      navigate(requiresPasswordChange ? "/trocar-senha" : "/dashboard", { replace: true });
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <div className="login-layout">
        <section className="login-panel" aria-labelledby="login-title">
          <div className="login-card">
            <div className="login-brand">
              <img src="/logo-iedc-sem-fundo-4k.png" alt="Dias da Cruz — Instituto Espírita" />
            </div>

            <div className="login-card__intro">
              <h2 id="login-title">Portal dos Associados</h2>
              <p>Acesse sua conta</p>
            </div>

            {!isOnline && <StatusBanner tone="warning">Sem conexão com a internet no momento.</StatusBanner>}
            {sessionExpiredNotice && <StatusBanner tone="info">Sua sessão expirou. Faça login novamente.</StatusBanner>}
            {error && <StatusBanner tone="error">{error}</StatusBanner>}

            <form onSubmit={handleSubmit} noValidate>
              <TextField
                label="Usuário"
                name="username"
                autoComplete="username"
                placeholder="Digite seu usuário"
                required
                leadingIcon={<UserIcon />}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
              <TextField
                label="Senha"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Digite sua senha"
                required
                leadingIcon={<LockIcon />}
                trailingAction={
                  <button
                    className="login-password-toggle"
                    type="button"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    <EyeIcon crossed={!showPassword} />
                  </button>
                }
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button type="submit" loading={loading} disabled={!isOnline} className="login-submit">
                {!loading && <><span>Entrar</span><ArrowRightIcon /></>}
              </Button>
            </form>

            <p className="login-card__help">Precisa de ajuda? Fale com a Secretaria</p>
          </div>
        </section>
      </div>
    </main>
  );
}
